import React, { useEffect, useState } from "react";
import { fetchMyProgress } from "../lib/api";

/**
 * TierProgressBar — shows current tier badge, neon progress bar inside the
 * current tier band, and "X MMR / ~N wins until {NextTier}".
 *
 * Auto-fetches `/api/me/progress` on mount. Hide gracefully if the user
 * doesn't exist or the endpoint errors — the dashboard already has the
 * tier badge elsewhere.
 */
export function TierProgressBar({ data, className = "" }) {
  const [p, setP] = useState(data || null);
  useEffect(() => {
    if (data) { setP(data); return; }
    let alive = true;
    fetchMyProgress().then((d) => { if (alive) setP(d); }).catch(() => { /* hide silently */ });
    return () => { alive = false; };
  }, [data]);
  if (!p) return null;
  const cur = p.current_tier || {};
  const nxt = p.next_tier;
  const pct = Math.max(0, Math.min(100, Number(p.band_progress_pct) || 0));
  return (
    <div
      data-testid="tier-progress-bar"
      className={`glass-strong rounded-3xl p-5 border-2 ${className}`}
      style={{ borderColor: `${cur.color || "#14b8a6"}66` }}
    >
      <div className="flex items-center justify-between gap-3">
        <div className="min-w-0">
          <p className="text-[10px] tracking-[3px] font-bold" style={{ color: cur.color || "#14b8a6" }}>
            <i className="fas fa-arrow-trend-up mr-1" />TIER PROGRESS
          </p>
          <p className="font-display text-2xl text-white mt-1 flex items-center gap-2">
            <span>{cur.badge}</span>
            <span style={{ color: cur.color || "#14b8a6" }}>{cur.name || "Unranked"}</span>
            <span className="text-white/40 text-base">· {p.mmr} MMR</span>
          </p>
        </div>
        {nxt && (
          <div className="text-right shrink-0">
            <p className="text-[10px] tracking-widest text-white/45">NEXT</p>
            <p className="font-display text-base flex items-center gap-1 justify-end">
              <span>{nxt.badge}</span>
              <span style={{ color: nxt.color || "#fff" }}>{nxt.name}</span>
            </p>
          </div>
        )}
      </div>

      <div className="mt-4 h-2 rounded-full bg-white/10 overflow-hidden">
        <div
          data-testid="tier-progress-fill"
          className="h-full transition-all duration-700"
          style={{
            width: `${pct}%`,
            background: `linear-gradient(90deg, ${cur.color || "#14b8a6"} 0%, ${nxt?.color || "#14b8a6"} 100%)`,
            boxShadow: `0 0 12px ${cur.color || "#14b8a6"}`,
          }}
        />
      </div>

      <div className="mt-3 flex flex-wrap justify-between text-[11px]">
        {nxt ? (
          <>
            <span className="text-white/65">
              <i className="fas fa-bolt mr-1 text-[#14b8a6]" />
              <b className="text-white">{p.mmr_to_next}</b> MMR until {nxt.name}
            </span>
            <span className="text-white/65" data-testid="tier-progress-wins-to-promo">
              <i className="fas fa-trophy mr-1 text-[#14b8a6]" />
              ~{p.matches_to_promotion_estimate} {p.matches_to_promotion_estimate === 1 ? "win" : "wins"} to promotion
            </span>
          </>
        ) : (
          <span className="text-[#14b8a6] font-bold">
            <i className="fas fa-crown mr-1" />Top of the ladder. Defend your title.
          </span>
        )}
      </div>
      {p.matches_played_season != null && (
        <p className="text-[10px] text-white/40 mt-2">
          <i className="fas fa-calendar-days mr-1" />
          {p.matches_played_season} matches played this season (last 28 days)
        </p>
      )}
    </div>
  );
}

export default TierProgressBar;
